import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Users } from 'lucide-react';
import PersonForm from '../../components/forms/PersonForm';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const PersonRegisterPage = () => {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (personData) => {
    if (!company?.id) {
      toast.error('Empresa não encontrada');
      return;
    }

    try {
      setLoading(true);
      
      console.log('Creating person with data:', personData);

      const createdPerson = await dataHelpers.createPerson(company.id, personData);
      
      console.log('Person created successfully:', createdPerson);
      
      toast.success('Pessoa cadastrada com sucesso!');
      
      navigate('/dashboard/people', { 
        state: { 
          message: 'Pessoa cadastrada com sucesso!',
          personId: createdPerson.id 
        }
      });
      
    } catch (error) {
      console.error('Error creating person:', error);
      
      let errorMessage = 'Erro ao cadastrar pessoa';
      
      if (error.message?.includes('duplicate')) {
        errorMessage = 'Já existe uma pessoa com este email ou documento';
      } else if (error.message?.includes('email')) {
        errorMessage = 'Este email já está cadastrado no sistema';
      } else if (error.message?.includes('document')) {
        errorMessage = 'Este documento já está cadastrado no sistema';
      } else if (error.message?.includes('company_id')) {
        errorMessage = 'Erro de autorização. Verifique se você tem permissão para cadastrar pessoas';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard/people');
  };

  return (
    
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/people')}
              className="flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Cadastrar Pessoa</h1>
              <p className="text-gray-600">
                Adicione uma nova pessoa ao sistema
              </p>
            </div>
          </div>
        </div>

        {/* Informações da empresa */}
        {company && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-blue-800">
                Cadastrando pessoa para: <strong>{company.name}</strong>
              </span>
            </div>
          </div>
        )}

        {/* Formulário */}
        <PersonForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
        />

        {/* Dicas */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">💡 Dicas para cadastro de pessoas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Tipos de Pessoa</h4>
              <ul className="space-y-1">
                <li>• <strong>Cliente:</strong> Compra produtos/serviços</li>
                <li>• <strong>Fornecedor:</strong> Fornece produtos/serviços</li>
                <li>• <strong>Usuário:</strong> Acesso ao sistema</li>
                <li>• <strong>Aluno:</strong> Estudante da instituição</li>
                <li>• <strong>Responsável:</strong> Responsável por aluno</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Documentos</h4>
              <ul className="space-y-1">
                <li>• CPF para pessoas físicas</li>
                <li>• CNPJ para fornecedores</li>
                <li>• Documentos devem ser únicos</li>
                <li>• Validação automática</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Endereço</h4>
              <ul className="space-y-1">
                <li>• Use o CEP para busca automática</li>
                <li>• Complemente com número e complemento</li>
                <li>• Dados opcionais mas recomendados</li>
                <li>• Facilita entregas e comunicação</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Gestão</h4>
              <ul className="space-y-1">
                <li>• Mantenha dados atualizados</li>
                <li>• Use observações para contexto</li>
                <li>• Desative pessoas inativas</li>
                <li>• Organize por tipo para facilitar busca</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    
  );
};

export default PersonRegisterPage;
